import "reflect-metadata"; // Always on top
import { createApp } from "vue";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import App from "./App.vue";
import router from "./router";
import { useE2eStore } from "./stores/e2eStore";
import DEPENDENCYTYPES from "@/common/dependency.types";
import type IWorkspaceStore from "@/common/services/Workspace/IWorkspaceStore";
import { WorkspaceStore } from "./stores/WorkspaceStore";
import { WorkspaceMetadataStore } from "./stores/WorkspaceMetadataStore";
import type IWorkspaceMetadataStore from "@/common/services/Workspace/IWorkspaceMetadataStore";
import SsoRedirectFactory from "./states/sso-redirect/ssoRedirect.factory";
import EnrollmentCipFactory from "./app/factories/enrollment/EnrollmentCipFactory";
import OpenAuthFactory from "@/open/app/factories/authorization/OpenAuthFactory";

import bltForm from "@/open/components/bltForm1.vue";
import bltWizard from "@/open/components/bltWizard.vue";
import openToolbar from "@/open/components/open-toolbar/openToolbar.vue";
import footerComponent from "@/open/components/footer-component/footer-component.vue";
import { useAllDirectives } from "@/common/directives/appch.directives.use";
import { useAllComponents } from "@/common/components/appch.components.use";
import APP_CODES from "@/common/enums/appCodeEnum";
import type IRouterService from "@/common/services/utils/IRouterService";
import RouterService from "./router/RouterService";
import GlobalError from "./handlers/GlobalError";
import { BltProgressBar, BannoEnrollmentForm } from "./components/open.components.module";
import { PlaidStore } from "@/open/stores/PlaidStore";
import type { IPlaidStore } from "@/common/services/Vendor/Plaid/IPlaidStore";
import bltScreen from "@/open/components/bltScreen.vue";
import { VueMaskDirective } from "v-mask";
import type IHttpErrorHandler from "@/common/services/connect/IHttpErrorHandler";
import OpenHttpErrorHandler from "@/open/services/OpenHttpErrorHandler";
import {CipStore, type ICipStore} from "@/open/stores/CipStore";
import type HttpHandlerConfig from "@/common/interfaces/httpHandler/HttpHandlerConfig";
import ApplicantChannelHttpHandlerConfig from "@/open/config/ApplicantChannelHttpHandlerConfig";
import {LDPlugin} from 'launchdarkly-vue-client-sdk'
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {container} from '@/common/ioc'
import type ActiveChannel from '@/common/interfaces/ActiveChannel'
import ActiveChannelApplicant from '@/open/config/ActiveChannelApplicant'
import LoggingService from '@/common/services/Log/LoggingService'

const app = createApp(App);

const PRODUCTION_PUBLIC_LAUNCH_DARKLY_CLIENT_ID = '67d18ec96e6a000973a6fbed';

app.config.globalProperties.window = <any>window;
(<any>window).APP_CODE = APP_CODES.OPEN;
app.config.globalProperties.$institutionConfig = window.institutionConfig;

/* To stay on the same Url when browser back button click */
window.onpopstate = function () {
  history.go(0);
};

app.config.errorHandler = GlobalError.handler;
const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);
app.use(pinia);
app.directive("mask", VueMaskDirective);

useAllDirectives(app);
useAllComponents(app);
app.component("blt-form", bltForm);
app.component("blt-wizard", bltWizard);
app.component("open-toolbar", openToolbar);
app.component("footerComponent", footerComponent);
app.component("blt-progress-bar", BltProgressBar);
app.component("banno-enrollment-form", BannoEnrollmentForm);
app.component("blt-screen", bltScreen);

container.bind<ActiveChannel>(DEPENDENCYTYPES.ActiveChannel)
  .to(ActiveChannelApplicant)
  .inSingletonScope()

container.bind<IHttpErrorHandler>(DEPENDENCYTYPES.IHttpErrorHandler).to(OpenHttpErrorHandler).inSingletonScope();
container.bind<IWorkspaceStore>(DEPENDENCYTYPES.IWorkspaceStore).toConstantValue(WorkspaceStore());
container
  .bind<IWorkspaceMetadataStore>(DEPENDENCYTYPES.IWorkspaceMetadataStore)
  .toConstantValue(WorkspaceMetadataStore());
container.bind<IPlaidStore>(DEPENDENCYTYPES.IPlaidStore).toConstantValue(PlaidStore());
container.bind<IRouterService>(DEPENDENCYTYPES.IRouterService).to(RouterService).inSingletonScope();
container.bind<HttpHandlerConfig>(DEPENDENCYTYPES.HttpHandlerConfig).to(ApplicantChannelHttpHandlerConfig).inSingletonScope();
container.bind<ICipStore>(DEPENDENCYTYPES.ICipStore).toConstantValue(CipStore())
container.bind<RouterService>(RouterService).to(RouterService).inSingletonScope();

container.get(LoggingService).configure({ appChannel: 'APPLICANT', institutionId: window.INSTITUTION_ID ?? '' })

app.use(router);

// We fall back to the production client id if a client id is not provided so that in case of production misconfiguration, we have a safe fallback.

const key = window.LAUNCH_DARKLY_PUBLIC_CLIENT_ID || PRODUCTION_PUBLIC_LAUNCH_DARKLY_CLIENT_ID
const host = window.location.hostname
const institutionId = window.INSTITUTION_ID

const context = institutionId
  ? {
      kind: 'multi',
      user: {
        kind: 'user',
        key: host,
        custom: {
          host: host
        }
      },
      institutionid: {
        key: institutionId
      }
    }
  : {
      kind: 'user',
      key: host,
      custom: {
        host: host
      }
    }

app.use(LDPlugin, {
  clientSideID: key,
  context
})

// Extends dayjs with the plugins we use.
dayjs.extend(duration)
dayjs.extend(utc)
dayjs.extend(customParseFormat)

app.mount("#app");


if ((window as any).Cypress) {
  (window as any).e2e = useE2eStore();
  (window as any).workspaceMetadataStore = WorkspaceMetadataStore();
  (window as any).workspaceStore = WorkspaceStore();
}
